import type { ShipmentPiece, ShipmentPieceStatus } from '@/apis/types';
import {
  inferCarrierFromTrackingId,
  type InferredCarrier,
} from '@/common/utils/inferCarrierFromTrackingId';
import type { StatusType } from '../statusType';

const PIECE_STATUS_META: Record<
  ShipmentPieceStatus,
  { label: string; type: StatusType }
> = {
  label_created: { label: 'Label created', type: 7 },
  in_transit: { label: 'In transit', type: 6 },
  out_for_delivery: { label: 'Out for delivery', type: 6 },
  delivered: { label: 'Delivered', type: 3 },
  exception: { label: 'Exception', type: 0 },
  returned: { label: 'Returned', type: 1 },
};

export const getPieceStatusMeta = (status?: string) =>
  PIECE_STATUS_META[status as ShipmentPieceStatus] ?? {
    label: status || '--',
    type: 7 as StatusType,
  };

export const countDeliveredPieces = (pieces: ShipmentPiece[]): number =>
  pieces.filter((piece) => piece.status === 'delivered').length;

const TRACKING_URLS: Partial<Record<InferredCarrier, (id: string) => string>> =
  {
    FedEx: (id) => `https://www.fedex.com/fedextrack/?trknbr=${id}`,
    UPS: (id) => `https://www.ups.com/track?tracknum=${id}`,
    USPS: (id) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${id}`,
  };

const CARRIERS_BY_TITLE: Record<string, InferredCarrier> = {
  fedex: 'FedEx',
  ups: 'UPS',
  usps: 'USPS',
};

/** Carrier tracking page for one box; the courier title wins over guessing from the number. */
export const getPieceTrackingUrl = (
  courierTitle: string | null | undefined,
  trackingNumber: string
): string | null => {
  const carrier =
    CARRIERS_BY_TITLE[(courierTitle ?? '').trim().toLowerCase()] ??
    inferCarrierFromTrackingId(trackingNumber);
  const build = carrier ? TRACKING_URLS[carrier] : undefined;
  return build ? build(encodeURIComponent(trackingNumber.trim())) : null;
};
